"use client";

import { AnimatedBadge } from "@/components/motion/animated-badge";

const row1 = [
  {
    avatar: "https://i.pravatar.cc/150?img=47",
    text: "Speakers who finish and wonder how it landed",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=1",
    text: "The room that stayed silent — now you know why",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=5",
    text: "'I'll email you the slides' — a phrase we're retiring",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=12",
    text: "Presenters tired of feedback that never comes",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=32",
    text: "The talk deserved better than a PDF",
  },
];

const row2 = [
  {
    avatar: "https://i.pravatar.cc/150?img=15",
    text: "Lecturers reading a hall of 200 students",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=20",
    text: "Sales reps whose deck vanishes into inboxes",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=25",
    text: "Trainers proving the material actually landed",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=8",
    text: "Event speakers who want signal, not applause",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=44",
    text: "L&D leads past the completion-rate lie",
  },
];

const row3 = [
  {
    avatar: "https://i.pravatar.cc/150?img=13",
    text: "Feedback before the room empties",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=23",
    text: "Slides your audience actually keeps",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=33",
    text: "The handout that outlives the talk",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=52",
    text: "Comments attached to the exact slide",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=60",
    text: "One QR on screen. Whole room in.",
  },
];

const row4 = [
  {
    avatar: "https://i.pravatar.cc/150?img=17",
    text: "'Slide 12 raised three questions' — now you know",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=68",
    text: "Rewriting slide 7 before the next cohort",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=45",
    text: "Reading the train-home reactions afterwards",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=64",
    text: "Reactions on slide 7 while you present slide 8",
  },
  {
    avatar: "https://i.pravatar.cc/150?img=36",
    text: "The deck that gets re-opened, not deleted",
  },
];

interface PillProps {
  avatar: string;
  text: string;
}

function Pill({ avatar, text }: PillProps) {
  return (
    <div className="flex items-center gap-3 bg-neutral-100 rounded-full pl-1.5 pr-6 py-1.5 flex-shrink-0 ">
      <img
        src={avatar}
        alt=""
        className="w-11 h-11 rounded-full object-cover flex-shrink-0"
        draggable={false}
      />
      <span className="text-neutral-900 text-base md:text-lg whitespace-nowrap font-medium">
        {text}
      </span>
    </div>
  );
}

interface MarqueeRowProps {
  items: PillProps[];
  direction?: "left" | "right";
  duration?: number;
}

function MarqueeRow({
  items,
  direction = "left",
  duration = 45,
}: MarqueeRowProps) {
  const doubled = [...items, ...items];
  const animationName = direction === "left" ? "marquee-left" : "marquee-right";

  return (
    <div className="relative overflow-hidden py-2 group">
      <div
        className="flex gap-4 w-max hover:[animation-play-state:paused]"
        style={{
          animation: `${animationName} ${duration}s linear infinite`,
        }}
      >
        {doubled.map((item, idx) => (
          <Pill
            key={`${direction}-${idx}`}
            avatar={item.avatar}
            text={item.text}
          />
        ))}
      </div>
    </div>
  );
}

export function PresenterMarquee() {
  return (
    <div className="w-full py-24">
      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>

      <div className="mx-auto w-full max-w-4xl px-4 mb-14 text-center flex flex-col gap-3">
        <AnimatedBadge status="info" size="sm" showIcon={false} className="w-fit">Who this is for</AnimatedBadge>
        <h2 className="text-4xl md:text-5xl font-medium text-neutral-900 leading-tight">
          Become part of presenters all around the world.
        </h2>
        <p className="text-lg text-neutral-500 leading-relaxed">
          The ones who stopped guessing and started reading the room.
        </p>
      </div>

      <div className="relative w-full">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-24 md:w-40 z-10"
          style={{
            background:
              "linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))",
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-24 md:w-40 z-10"
          style={{
            background:
              "linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))",
          }}
        />

        <div className="flex flex-col gap-4">
          <MarqueeRow items={row1} direction="left" duration={50} />
          <MarqueeRow items={row2} direction="right" duration={55} />
          <MarqueeRow items={row3} direction="left" duration={48} />
          <MarqueeRow items={row4} direction="right" duration={52} />
        </div>
      </div>
    </div>
  );
}
